import React, { useState, useEffect } from 'react';
import { styled } from 'styletron-react';
import { Input } from 'baseui/input';
import { Button } from 'baseui/button';
import { FormControl } from 'baseui/form-control';
import { Select } from 'baseui/select';
import { Spinner } from 'baseui/spinner';
import { HeadingMedium, ParagraphSmall } from 'baseui/typography';
import { Save, X, Building2, Settings as SettingsIcon, Globe, Key, ChevronRight, Shield, RefreshCw, DoorOpen } from 'lucide-react';
import { useTenant } from '../../hooks/useTenant';
import { JsonSettingsEditor } from '../../components/JsonSettingsEditor';
import { toaster } from 'baseui/toast';
import { Content } from '../LayoutPage';
import { directApiInstance } from '../../api/httpClient';
import { S3ImageUpload } from '../../components/S3ImageUpload';
import { useT } from '../../i18n/admin';

// ── Styled Components ──

const Page = styled('div', {
  backgroundColor: '#F9FAFB',
  height: '100%',
  overflow: 'auto',
});

const Container = styled('div', {
  maxWidth: '900px',
  margin: '0 auto',
  padding: '24px 24px 60px',
});

const Header = styled('div', {
  marginBottom: '24px',
});

const StickyFooter = styled('div', {
  position: 'sticky',
  bottom: 0,
  backgroundColor: '#F9FAFB',
  padding: '16px 0',
  borderTop: '1px solid #E5E7EB',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '24px',
});

const Card = styled('div', {
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  marginBottom: '16px',
  overflow: 'hidden',
});

const CardHeader = styled('div', {
  padding: '16px 20px',
  borderBottom: '1px solid #F3F4F6',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

const CardTitle = styled('h3', {
  margin: 0,
  fontSize: '15px',
  fontWeight: 600,
  color: '#111827',
});

const CardDescription = styled('p', {
  margin: '2px 0 0',
  fontSize: '12px',
  color: '#9CA3AF',
});

const CardBody = styled('div', {
  padding: '20px',
});

const FormGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  '@media (max-width: 640px)': {
    gridTemplateColumns: '1fr',
  },
});

const FormGridFull = styled('div', {
  gridColumn: '1 / -1',
});

// SAM-1312 (U29): a real button (role switch), not a clickable div.
const Toggle = styled('button', ({ $active }: { $active: boolean }) => ({
  border: 'none',
  padding: 0,
  width: '44px',
  height: '24px',
  borderRadius: '12px',
  backgroundColor: $active ? 'var(--tenant-primary, #111827)' : '#D1D5DB',
  position: 'relative' as const,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  flexShrink: 0,
}));

const ToggleThumb = styled('div', ({ $active }: { $active: boolean }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: '#fff',
  position: 'absolute' as const,
  top: '2px',
  left: $active ? '22px' : '2px',
  transition: 'left 0.2s ease',
  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
}));

const ToggleRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0',
});

const ToggleLabel = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const MetaRow = styled('div', {
  display: 'flex',
  gap: '24px',
  fontSize: '12px',
  color: '#9CA3AF',
  marginTop: '12px',
});

const Badge = styled('span', ({ $color }: { $color: string }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 8px',
  borderRadius: '10px',
  fontSize: '11px',
  fontWeight: 500,
  backgroundColor: $color === 'green' ? '#D1FAE5' : $color === 'blue' ? '#DBEAFE' : '#F3F4F6',
  color: $color === 'green' ? '#065F46' : $color === 'blue' ? '#1E40AF' : '#6B7280',
}));

const TenantSwitcher = styled('div', {
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  padding: '12px 20px',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const AccessLevelCard = styled('div', ({ $selected }: { $selected: boolean }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 14px',
  borderRadius: '8px',
  border: `1px solid ${$selected ? 'var(--tenant-primary, #111827)' : '#E5E7EB'}`,
  backgroundColor: $selected ? '#F9FAFB' : '#fff',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  ':hover': { borderColor: 'var(--tenant-primary, #111827)', backgroundColor: '#FAFAFA' },
}));

const AccessLevelCheckbox = styled('div', ({ $checked }: { $checked: boolean }) => ({
  width: '18px',
  height: '18px',
  borderRadius: '4px',
  border: `2px solid ${$checked ? 'var(--tenant-primary, #111827)' : '#D1D5DB'}`,
  backgroundColor: $checked ? 'var(--tenant-primary, #111827)' : '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'all 0.15s ease',
}));

const LoadingContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
});

// ── Component ──

export const TenantSettings: React.FC = () => {
  const { fmt } = useT();
  const {
    tenant, allTenants, currentTenantId, hasMultipleTenants,
    isLoading, error, updateTenant, isUpdating, switchTenant,
  } = useTenant();

  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    settings: {} as Record<string, any>,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [accessLevels, setAccessLevels] = useState<any[]>([]);
  const [loadingLevels, setLoadingLevels] = useState(false);

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        domain: tenant.domain || '',
        settings: tenant.settings || {},
      });
      setHasChanges(false);
    }
  }, [tenant]);

  const handleInputChange = (field: 'name' | 'domain', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSettingsChange = (settings: Record<string, any>) => {
    setFormData(prev => ({ ...prev, settings }));
    setHasChanges(true);
  };

  const updateHikField = (field: string, value: any) => {
    const hik = { ...(formData.settings?.hikConnect || {}), [field]: value };
    handleSettingsChange({ ...formData.settings, hikConnect: hik });
  };

  const handleSave = () => {
    if (!tenant?.id) {
      toaster.negative('No tenant selected', { autoHideDuration: 2000 });
      return;
    }
    if (!formData.name.trim()) {
      toaster.warning('Tenant name is required', { autoHideDuration: 2000 });
      return;
    }
    updateTenant(tenant.id, {
      name: formData.name,
      domain: formData.domain || undefined,
      settings: formData.settings,
    }, {
      // S-16: a refused save (403) keeps the form dirty, next to the error toast.
      onSuccess: () => setHasChanges(false),
    });
  };

  const handleCancel = () => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        domain: tenant.domain || '',
        settings: tenant.settings || {},
      });
      setHasChanges(false);
    }
  };

  const fetchAccessLevels = async () => {
    setLoadingLevels(true);
    try {
      const resp = await directApiInstance({
        url: '/hik/access-levels',
        method: 'GET',
      }) as any;
      const levels = resp?.data?.levels || resp?.levels || [];
      setAccessLevels(levels);
      if (levels.length === 0) {
        toaster.warning('No access levels found. Verify your Hik-Connect credentials.', { autoHideDuration: 3000 });
      }
    } catch (err: any) {
      toaster.negative('Failed to load access levels. Save credentials first.', { autoHideDuration: 3000 });
      setAccessLevels([]);
    } finally {
      setLoadingLevels(false);
    }
  };

  const toggleAccessLevel = (levelId: string) => {
    const current = formData.settings?.hikConnect?.accessLevelIds || [];
    const updated = current.includes(levelId)
      ? current.filter((id: string) => id !== levelId)
      : [...current, levelId];
    updateHikField('accessLevelIds', updated);
  };

  const selectedLevelIds: string[] = formData.settings?.hikConnect?.accessLevelIds || [];
  const hikEnabled = formData.settings?.hikConnect?.enabled || false;

  if (isLoading) {
    return <Content><Page><Container><LoadingContainer><Spinner /></LoadingContainer></Container></Page></Content>;
  }

  if (error || !tenant) {
    return (
      <Content>
        <Page>
          <Container>
            <div style={{ textAlign: 'center', padding: '80px 20px', color: '#6B7280' }}>
              <Building2 size={48} color="#D1D5DB" style={{ marginBottom: 12 }} />
              <h2 style={{ color: '#111', marginBottom: 8 }}>{error ? 'Error loading tenant' : 'No tenant found'}</h2>
              <p>{error?.toString() || 'You don\'t have access to any tenant configuration.'}</p>
            </div>
          </Container>
        </Page>
      </Content>
    );
  }

  return (
    <Content>
      <Page>
        <Container>
          <Header>
            <HeadingMedium margin={0}>Tenant Settings</HeadingMedium>
            <ParagraphSmall marginTop="4px" marginBottom={0} color="#6B7280">
              Manage your tenant configuration and integrations
            </ParagraphSmall>
          </Header>

          {/* Tenant Switcher */}
          {hasMultipleTenants && allTenants && (
            <TenantSwitcher>
              <Shield size={16} color="#6B7280" />
              <div style={{ flex: 1 }}>
                <Select
                  options={allTenants.map(t => ({ id: t.id, label: t.name }))}
                  value={allTenants.filter(t => t.id === currentTenantId).map(t => ({ id: t.id, label: t.name }))}
                  onChange={({ value }) => { if (value.length > 0) switchTenant(value[0].id as string); }}
                  placeholder="Select tenant"
                  size="compact"
                  clearable={false}
                />
              </div>
            </TenantSwitcher>
          )}

          {/* General */}
          <Card>
            <CardHeader>
              <Building2 size={18} color="#6B7280" />
              <div>
                <CardTitle>General</CardTitle>
                <CardDescription>Basic tenant information</CardDescription>
              </div>
            </CardHeader>
            <CardBody>
              <FormGrid>
                <FormControl label="Tenant Name">
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', (e.target as HTMLInputElement).value)}
                    placeholder="Enter tenant name"
                    disabled={isUpdating}
                    size="compact"
                  />
                </FormControl>
                <FormControl label="Domain">
                  <Input
                    value={formData.domain}
                    onChange={(e) => handleInputChange('domain', (e.target as HTMLInputElement).value)}
                    placeholder="example.com"
                    disabled={isUpdating}
                    size="compact"
                    startEnhancer={() => <Globe size={14} color="#9CA3AF" />}
                  />
                </FormControl>
                <FormControl label="Primary Color">
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={formData.settings?.primaryColor || '#000000'}
                      onChange={(e) => handleSettingsChange({ ...formData.settings, primaryColor: e.target.value })}
                      style={{ width: '40px', height: '32px', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer', padding: '2px' }}
                    />
                    <Input
                      value={formData.settings?.primaryColor || '#000000'}
                      onChange={(e) => handleSettingsChange({ ...formData.settings, primaryColor: (e.target as HTMLInputElement).value })}
                      placeholder="#000000"
                      disabled={isUpdating}
                      size="compact"
                    />
                  </div>
                </FormControl>
                <FormGridFull>
                  <S3ImageUpload
                    value={formData.settings?.logo || ''}
                    onChange={(key) => handleSettingsChange({ ...formData.settings, logo: key })}
                    label="Tenant Logo"
                    previewHeight="120px"
                    objectFit="contain"
                  />
                </FormGridFull>
                <FormGridFull>
                  <S3ImageUpload
                    value={formData.settings?.favicon || ''}
                    onChange={(key) => handleSettingsChange({ ...formData.settings, favicon: key })}
                    label="Favicon (small icon for browser tab)"
                    previewHeight="48px"
                    objectFit="contain"
                  />
                </FormGridFull>
              </FormGrid>
              {(tenant.created_at || tenant.updated_at) && (
                <MetaRow>
                  {tenant.created_at && <span>Created {fmt.date(tenant.created_at)}</span>}
                  {tenant.updated_at && <span>Updated {fmt.date(tenant.updated_at)}</span>}
                </MetaRow>
              )}
            </CardBody>
          </Card>

          {/* Hik-Connect */}
          <Card>
            <CardHeader>
              <Key size={18} color="#6B7280" />
              <div style={{ flex: 1 }}>
                <CardTitle>Hik-Connect</CardTitle>
                <CardDescription>Automatic PIN code generation for access control</CardDescription>
              </div>
              <Badge $color={hikEnabled ? 'green' : 'gray'}>{hikEnabled ? 'Enabled' : 'Disabled'}</Badge>
            </CardHeader>
            <CardBody>
              <ToggleRow>
                <ToggleLabel>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>Enable Hik-Connect Integration</span>
                  <span style={{ fontSize: '12px', color: '#9CA3AF' }}>Generate temporary PIN codes for invitations and business access</span>
                </ToggleLabel>
                <Toggle type="button" role="switch" aria-checked={hikEnabled} aria-label="Hik-Connect" $active={hikEnabled} onClick={() => updateHikField('enabled', !hikEnabled)}>
                  <ToggleThumb $active={hikEnabled} />
                </Toggle>
              </ToggleRow>

              {hikEnabled && (
                <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: '16px', marginTop: '8px' }}>
                  <FormGrid>
                    <FormControl label="App Key">
                      <Input
                        value={formData.settings?.hikConnect?.appKey || ''}
                        onChange={(e: any) => updateHikField('appKey', e.currentTarget.value)}
                        size="compact"
                        placeholder="Your Hik-Connect App Key"
                      />
                    </FormControl>
                    <FormControl label="Secret Key">
                      <Input
                        value={formData.settings?.hikConnect?.secretKey || ''}
                        onChange={(e: any) => updateHikField('secretKey', e.currentTarget.value)}
                        size="compact"
                        type="password"
                        placeholder="Your Hik-Connect Secret Key"
                      />
                    </FormControl>
                    <FormControl label="Base URL">
                      <Input
                        value={formData.settings?.hikConnect?.baseUrl || 'https://ius.hik-connect.com'}
                        onChange={(e: any) => updateHikField('baseUrl', e.currentTarget.value)}
                        size="compact"
                      />
                    </FormControl>
                    {/* SAM-426: 3 uses by default (the backend falls back to 3 when unset). */}
                    <FormControl label="Default Open Count" caption="Number of times a PIN can be used (default 3)">
                      <Input
                        type="number"
                        min={1}
                        placeholder="3"
                        value={formData.settings?.hikConnect?.defaultOpenCount || 3}
                        onChange={(e: any) => updateHikField('defaultOpenCount', parseInt(e.currentTarget.value) || 3)}
                        size="compact"
                      />
                    </FormControl>
                    {/* SAM-306: a one-day pass lasts this many real hours from its start. */}
                    <FormControl label="One-Day Pass Hours" caption="Hours a one-day PIN stays valid from its start (default 24)">
                      <Input
                        type="number"
                        min={1}
                        placeholder="24"
                        value={formData.settings?.hikConnect?.oneDayPassHours || 24}
                        onChange={(e: any) => updateHikField('oneDayPassHours', Math.max(1, parseInt(e.currentTarget.value) || 24))}
                        size="compact"
                      />
                    </FormControl>
                    <FormGridFull>
                      <div style={{ marginTop: '4px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <div>
                            <span style={{ fontSize: '14px', fontWeight: 500 }}>Access Levels</span>
                            <span style={{ fontSize: '12px', color: '#9CA3AF', marginLeft: '8px' }}>
                              {selectedLevelIds.length > 0 ? `${selectedLevelIds.length} selected` : 'None selected'}
                            </span>
                          </div>
                          <Button
                            onClick={fetchAccessLevels}
                            size="mini"
                            kind="secondary"
                            isLoading={loadingLevels}
                            startEnhancer={() => <RefreshCw size={12} />}
                            overrides={{ BaseButton: { style: { borderRadius: '6px', fontSize: '12px' } } }}
                          >
                            {accessLevels.length > 0 ? 'Refresh' : 'Load Levels'}
                          </Button>
                        </div>

                        {accessLevels.length === 0 && !loadingLevels && (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px', border: '1px dashed #E5E7EB', borderRadius: '8px' }}>
                            <DoorOpen size={24} color="#D1D5DB" style={{ marginBottom: 8 }} />
                            <div>Click "Load Levels" to fetch available access levels from Hik-Connect</div>
                            <div style={{ fontSize: '11px', marginTop: 4 }}>Make sure App Key and Secret Key are saved first</div>
                          </div>
                        )}

                        {accessLevels.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {accessLevels.map((level: any) => {
                              const isSelected = selectedLevelIds.includes(level.id);
                              const devices = (level.associateResList || []).map((d: any) => d.name).join(', ');
                              return (
                                <AccessLevelCard
                                  key={level.id}
                                  $selected={isSelected}
                                  onClick={() => toggleAccessLevel(level.id)}
                                >
                                  <AccessLevelCheckbox $checked={isSelected}>
                                    {isSelected && (
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    )}
                                  </AccessLevelCheckbox>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#111' }}>{level.name}</div>
                                    {level.remark && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: 2 }}>{level.remark}</div>}
                                    {devices && (
                                      <div style={{ fontSize: '11px', color: '#6B7280', marginTop: 3, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <DoorOpen size={10} /> {devices}
                                      </div>
                                    )}
                                  </div>
                                </AccessLevelCard>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </FormGridFull>
                  </FormGrid>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Advanced Settings */}
          <Card>
            <CardHeader>
              <SettingsIcon size={18} color="#6B7280" />
              <div>
                <CardTitle>Advanced Settings</CardTitle>
                <CardDescription>Custom key-value configuration (JSON)</CardDescription>
              </div>
            </CardHeader>
            <CardBody style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <JsonSettingsEditor
                value={formData.settings}
                onChange={handleSettingsChange}
                disabled={isUpdating}
              />
            </CardBody>
          </Card>

          {/* Sticky Save Bar */}
          {hasChanges && (
            <StickyFooter>
              <Button
                onClick={handleCancel}
                disabled={isUpdating}
                kind="secondary"
                size="compact"
                startEnhancer={() => <X size={14} />}
              >
                Discard
              </Button>
              <Button
                onClick={handleSave}
                disabled={isUpdating}
                isLoading={isUpdating}
                size="compact"
                startEnhancer={() => <Save size={14} />}
                overrides={{
                  BaseButton: {
                    style: { backgroundColor: 'var(--tenant-primary, #111827)', ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' } },
                  },
                }}
              >
                Save Changes
              </Button>
            </StickyFooter>
          )}
        </Container>
      </Page>
    </Content>
  );
};
